import type Filter from './Filter';
import { type FilterDefinitions } from './components/Filters';

/**
 * Manages a collection of filter values.
 * Detects values from available records, checks whether records match set filters,
 * and returns filter definitions in a way that the filter components can use.
 */
export default class Filters<RecordType = any> {
  defaults: Record<string, any>;
  filters: Record<string, Filter & { matches(record: RecordType): boolean }>;
  definitions: FilterDefinitions;
  recordAdders: Record<string, any>;

  constructor() {
    this.defaults = {};
    this.filters = {};
    this.definitions = {};
    this.recordAdders = {};

    this.setDefaults();
  }

  get isActive() {
    return Object.keys(this.definitions).some(key => !this.isDefaultValue(key));
  }

  isDefaultValue(key: string) {
    // TODO: [JDW] I'm not sure this method works as intended

    const values = this.filters[key].toBookmarkConfiguration();
    const defaults = this.defaults[key] || [];

    return (
      values.length === defaults.length &&
      values.every(id => defaults.includes(id))
    );
  }

  matches(record: RecordType) {
    return Object.values(this.filters).every(f => f.matches(record));
  }

  getValue(key: string) {
    return this.filters[key].getValue();
  }

  getValues() {
    return Object.entries(this.filters).reduce((acc, [name, filter]) => {
      acc[name] = filter.getValue();
      return acc;
    }, {});
  }

  addFilterValue(name: string, item: any) {
    if (!this.filters[name]) throw new Error(`Filter ${name} does not exist`);
    // @ts-expect-error
    this.filters[name].addItem(item);
  }

  deleteFilterValue(name: string, item: any) {
    if (!this.filters[name]) throw new Error(`Filter ${name} does not exist`);
    // @ts-expect-error
    this.filters[name].deleteItem(item);
  }

  setFilterValues(name: string, newState: any[]) {
    if (this.filters[name]) {
      // @ts-expect-error
      this.filters[name].setItems(newState);
    }
  }

  clearFilterValues(name: string) {
    // @ts-expect-error
    this.filters[name].clearItems();
  }

  clearAll() {
    Object.keys(this.definitions).forEach(k => this.clearFilterValues(k));
    this.setDefaults();
  }

  setDefaults(overrides = {}) {
    const defaults = { ...this.defaults, ...overrides };

    Object.keys(defaults).forEach(key => {
      if (this.filters[key]) {
        // @ts-expect-error
        this.filters[key].setItems(defaults[key]);
      }
    });
  }

  toBookmarkConfiguration() {
    return Object.entries(this.filters).reduce((acc, [name, filter]) => {
      acc[name] = filter.toBookmarkConfiguration();
      return acc;
    }, {});
  }

  addRecords(records: any[]) {
    records.forEach(record => {
      this.addRecord(record);
    });
  }

  addRecord(record: any) {
    Object.keys(this.recordAdders).forEach(prop => {
      if (record[prop]) {
        this.recordAdders[prop](record);
      }
    });
  }
}
